/* eslint-disable no-console */
import { useState, useEffect } from 'react';

import { makeStyles } from '@material-ui/core/styles';
import { LoadingButton } from '@material-ui/lab';
import { Box, FormControl, InputLabel, Select, MenuItem, TextField } from '@material-ui/core';
import FilterListIcon from '@material-ui/icons/FilterList';
import { useDispatch, useSelector } from 'react-redux';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import moment from 'moment';
import { TextField as TextFieldDate } from '@mui/material';
import { CleanFilterButton } from '../library/CompactClearFilterButton';
import { getCsvDataIdentifications } from '../../utils/identificationsServices';
import { ExportButton } from '../library/ExportButton';
import { updateFilterOptions, resetPaginationData } from '../../redux/slices/identifications';
import { RootState } from '../../redux/store';

import { periodReferences } from '../../utils/periodDate';
import { useDateRange } from '../../utils/useDateRange';
import 'moment/locale/pt-br';

moment.locale('pt-br');

const periodOptions = Object.values(periodReferences);

const useStyles = makeStyles((theme) => ({
  table: {
    minWidth: 650,
  },
  formButton: {
    margin: theme.spacing(1),
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
  selectEmpty: {
    marginTop: theme.spacing(2),
  },
}));

export function IdentificationsFilter() {
  const dispatch = useDispatch();
  const classes = useStyles();
  const [exportRequesting, setExportRequesting] = useState(false);
  const [cpf, setCpf] = useState('');
  const [placeSelected, setPlaceSelected] = useState<any>('Todos');
  const [groupPlaces, setGroupPlaces] = useState<any>([]);

  const { reset } = useSelector((state: RootState) => state.global);

  const initialState = {
    period: 'ALL',
    cpf: '',
    place: '',
  };

  const [filter, setFilter] = useState(initialState);

  const isRangedDate = filter.period.split(':')[1];
  if (isRangedDate) {
    setFilter((state) => ({ ...state, period: 'BETWEEN_DATES' }));
  }

  const { valueFromDate, valueBeforeDate, periodDateRange, handleDateChange } = useDateRange();

  useEffect(() => {
    updatePlacesRequest();
  }, []);

  useEffect(() => {
    if (periodDateRange) {
      setFilter((state) => ({ ...state, period: 'BETWEEN_DATES' }));
    }
  }, [periodDateRange]);

  const updatePlacesRequest = () => {
    const selectedGroupPlaces = localStorage?.getItem('group-selected-places-id')?.length
      ? JSON.parse(localStorage.getItem('group-selected-places-id')!)
      : [];
    localStorage.setItem('selected-places-id', JSON.stringify(selectedGroupPlaces));
    setGroupPlaces(selectedGroupPlaces);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    if (name === 'period') {
      const periodValues = Object.values(periodReferences);
      const periodKeys = Object.keys(periodReferences);
      const foundIndexOf = periodValues.findIndex((item) => item === value);
      setFilter((state) => ({ ...state, [name]: periodKeys[foundIndexOf] }));
    } else if (name === 'cpf') {
      setCpf(value);
      setFilter((state) => ({ ...state, cpf: value }));
    } else if (name === 'place') {
      setPlaceSelected(value);
      if (value === 'Todos') localStorage.setItem('selected-places-id', JSON.stringify(groupPlaces));
      else localStorage.setItem('selected-places-id', JSON.stringify([value]));
      setFilter((state) => ({ ...state, place: value }));
    }
  };

  const renderList = (list) =>
    list.map((item, index) => (
      <MenuItem key={index} value={item}>
        {item}
      </MenuItem>
    ));

  const handleSubmit = (event) => {
    event.preventDefault();
    dispatch(resetPaginationData(true));
    if (filter.period === 'BETWEEN_DATES') filter.period = periodDateRange;
    dispatch(updateFilterOptions(filter, true));
  };

  const handleCleanFilter = () => {
    setFilter(initialState);
    setCpf('');
    setPlaceSelected('Todos');
    updatePlacesRequest();
    dispatch(resetPaginationData(true));
    dispatch(updateFilterOptions(initialState, true));
  };

  const handleExport = async () => {
    setExportRequesting(true);
    const result = await getCsvDataIdentifications({ period: initialState.period });
    if (result) {
      setExportRequesting(false);
      return result;
    }
  };

  useEffect(() => {
    if (reset === true) setFilter(initialState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reset]);

  return (
    <Box sx={{ mb: 1, mt: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <form
        style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}
        onSubmit={(event) => handleSubmit(event)}
      >
        <FormControl variant="outlined" className={classes.formControl}>
          <InputLabel id="payments-period">Período</InputLabel>
          <Select
            labelId="period-label"
            id="period"
            name="period"
            value={periodReferences[filter.period]}
            onChange={(event) => handleChange(event)}
            label="Período"
          >
            {renderList(periodOptions)}
          </Select>
        </FormControl>
        {filter.period === 'BETWEEN_DATES' && (
          <>
            <FormControl variant="outlined" className={classes.formControl}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  label="De"
                  inputFormat="DD/MM/YYYY"
                  value={valueFromDate?.toDate() || null}
                  onChange={(newDate) => handleDateChange(newDate ? moment(newDate) : null, true)}
                  renderInput={(params) => <TextFieldDate {...params} sx={{ width: '170px' }} />}
                />
              </LocalizationProvider>
            </FormControl>
            <FormControl variant="outlined" className={classes.formControl}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  label="Até"
                  inputFormat="DD/MM/YYYY"
                  value={valueBeforeDate?.toDate() || null}
                  onChange={(newDate) => handleDateChange(newDate ? moment(newDate) : null, false)}
                  renderInput={(params) => <TextFieldDate {...params} sx={{ width: '170px' }} />}
                />
              </LocalizationProvider>
            </FormControl>
          </>
        )}

        <FormControl>
          <TextField
            style={{ minWidth: '110px' }}
            required={false}
            value={cpf}
            label="Nome/CPF"
            onChange={(event) => handleChange(event)}
            id="cpf"
            name="cpf"
            className={classes.formControl}
          />
        </FormControl>
        <div hidden>
          <FormControl variant="outlined" className={classes.formControl}>
            <InputLabel id="payments-places">Estabelecimento</InputLabel>
            <Select
              labelId="place-label"
              id="place"
              name="place"
              value={placeSelected}
              onChange={(event) => handleChange(event)}
              label="Estabelecimento"
            >
              {renderList(['Todos', ...groupPlaces])}
            </Select>
          </FormControl>
        </div>
        <LoadingButton
          type="submit"
          color="primary"
          variant="outlined"
          size="medium"
          onClick={() => handleSubmit}
          startIcon={<FilterListIcon />}
          className={classes.formButton}
        >
          Filtrar
        </LoadingButton>
        <CleanFilterButton onClick={handleCleanFilter} />
      </form>
      <Box sx={{ display: 'flex', marginRight: 2 }}>
        <ExportButton name="Identificacoes" handleExport={handleExport} isRequesting={exportRequesting} />
      </Box>
    </Box>
  );
}
